import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useAuth } from "BEDROCK/auth/supabase-auth";
import { Checkout } from "./checkout";
const CHECKOUT_STORAGE_KEY = "checkout";

export type CheckoutError = "unaunthorized" | "unknown";

export type CheckoutItem = {
  id: string;
  sku: string;
  name: string;
  imageURL?: string;
  stripePriceID: string;
};

type CheckoutState = {
  items: CheckoutItem[];
};

type CheckoutContextType = {
  checkout: CheckoutState;
  addProject: (project: string) => void;
  updateItem: (project: CheckoutItem) => void;
  clearCheckout: () => void;
  isEmpty: boolean;
  pay: () => Promise<void>;
};

const CheckoutContext = createContext<CheckoutContextType | undefined>(
  undefined
);

export const CheckoutProvider = ({ children }: { children: ReactNode }) => {
  const [checkout, setCheckout] = useState<CheckoutState>(() => {
    const stored = localStorage.getItem(CHECKOUT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : { items: [] };
  });
  const { session } = useAuth();

  const addProject = (projectID: string) => {
    const item: CheckoutItem = {
      id: projectID,
      sku: "digital_download",
      name: "Digital Download",
      stripePriceID: "price_1RFtrF4G3b7h825KDWuvSIOQ",
    };
    const items = checkout.items || [];
    const existingItemIndex = items.findIndex((i) => i.id === item.id);
    if (existingItemIndex !== -1) {
      items[existingItemIndex] = item;
    } else {
      items.push(item);
    }
    setCheckout((prev) => {
      const newState = { ...prev, items };
      localStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(newState));
      return newState;
    });
  };

  const updateItem = (item: CheckoutItem) => {
    const items = checkout.items.map((i) => (i.id === item.id ? item : i));
    setCheckout((prev) => {
      const newState = { ...prev, items };
      localStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(newState));
      return newState;
    });
  };

  const clearCheckout = () => {
    localStorage.removeItem(CHECKOUT_STORAGE_KEY);
    setCheckout({ items: [] });
  };

  const pay = async () => {
    try {
      const token = session?.access_token;
      if (!token) {
        throw new Error("User is not authenticated");
      }
      const url = process.env.REACT_APP_CHECKOUT_URL!;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ items: checkout.items }),
      });
      if (!response.ok) {
        if (response.status === 401) {
          const err: CheckoutError = "unaunthorized";
          throw new Error(err);
        }
        throw new Error(`Request failed with status ${response.status}`);
      }
      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      const message: CheckoutError = "unknown";
      throw new Error(message);
    }
  };

  const isEmpty = checkout.items.length === 0;

  return (
    <CheckoutContext.Provider
      value={{ checkout, addProject, clearCheckout, isEmpty, pay, updateItem }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () => {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error("useCheckout must be used within a CheckoutProvider");
  }
  return context;
};
